'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import Link from 'next/link';
import { ChevronDown, Menu, X } from 'lucide-react';
import { AccountMenu } from '@/components/Layout/AccountMenu';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SITE_ICON_SVG_PATH, SITE_NAME } from '@/lib/structured-data/site-config';
import { PUBLIC_NAVIGATION, PROVIDER_NAVIGATION, QUOTE_URL } from './public-navigation.consts';

const PublicAccount = () => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return <div role="status" className="h-8 w-36 animate-pulse rounded bg-slate-100 motion-reduce:animate-none"><span className="sr-only">Loading account…</span></div>;
  }
  if (!isSignedIn) {
    return <Link href="/sign-in/" className="rounded px-2 py-1.5 text-xs font-medium text-slate-700 hover:text-[var(--ra-green-hover)] sm:text-sm"><span>Company sign in</span></Link>;
  }

  return <div className="flex items-center gap-2 sm:gap-3">
    <Link href="/account/" className="whitespace-nowrap rounded px-1 py-1.5 text-xs font-medium text-slate-700 hover:text-[var(--ra-green-hover)] sm:text-sm"><span>My workspace</span></Link>
    <AccountMenu compact />
  </div>;
};

export const PublicHeader = () => {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [providersOpen, setProvidersOpen] = useState(false);
  const mobileTrigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setProvidersOpen(false);
  }, [pathname]);

  const closeMobile = () => {
    setMobileOpen(false);
  };

  return <header className="sticky top-0 z-40 border-b border-slate-200 bg-white shadow-sm" onKeyDown={(event) => {
    if (event.key === 'Escape' && mobileOpen) {
      event.preventDefault();
      setMobileOpen(false);
      mobileTrigger.current?.focus();
    }
  }}>
    <div className="border-b border-slate-100 bg-slate-50">
      <div className="mx-auto flex h-10 max-w-7xl items-center justify-end gap-2 px-4 sm:gap-5 sm:px-6 lg:h-9 lg:px-8">
        <DropdownMenu open={providersOpen} onOpenChange={setProvidersOpen}>
          <DropdownMenuTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-950 sm:text-sm">
              <span>For logistics companies</span><ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-60">
            {PROVIDER_NAVIGATION.map((item) => {
              return <DropdownMenuItem key={item.href} asChild><Link href={item.href}><span>{item.label}</span></Link></DropdownMenuItem>;
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <PublicAccount />
      </div>
    </div>
    <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:h-16 lg:px-8">
      <Link href="/" className="flex shrink-0 items-center gap-2 rounded" aria-label={`${SITE_NAME} home`} onClick={closeMobile}>
        <img src={SITE_ICON_SVG_PATH} alt="" width={32} height={32} />
        <span className="hidden text-xl text-gray-800 sm:inline">{SITE_NAME}</span>
      </Link>
      <nav aria-label="Main navigation" className="ml-auto hidden items-center gap-7 lg:mr-4 lg:flex">
        {PUBLIC_NAVIGATION.map((item) => {
          const active = !item.href.includes('#') && pathname.startsWith(item.href);
          return <Link key={item.href} href={item.href} aria-current={active ? 'page' : undefined} className="rounded text-sm font-medium text-slate-600 hover:text-[var(--ra-green-hover)]"><span>{item.label}</span></Link>;
        })}
      </nav>
      <div className="flex items-center gap-3">
        <Link href={QUOTE_URL} onClick={closeMobile} className="whitespace-nowrap rounded-md bg-[var(--ra-green)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--ra-green-hover)]"><span>Get shipping quotes</span></Link>
        <button ref={mobileTrigger} type="button" aria-label={mobileOpen ? 'Close main menu' : 'Open main menu'} aria-expanded={mobileOpen} aria-controls="public-mobile-navigation" onClick={() => {
          setMobileOpen((open) => { return !open; });
        }} className="rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden">
          {mobileOpen ? <X aria-hidden="true" className="h-5 w-5" /> : <Menu aria-hidden="true" className="h-5 w-5" />}
        </button>
      </div>
    </div>
    {mobileOpen && <nav id="public-mobile-navigation" aria-label="Main navigation" className="border-t border-slate-100 bg-white px-4 py-3 lg:hidden">
      {PUBLIC_NAVIGATION.map((item) => {
        return <Link key={item.href} href={item.href} onClick={closeMobile} className="block rounded px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"><span>{item.label}</span></Link>;
      })}
    </nav>}
  </header>;
};
